import {
  SlideOutModal,
  SlideOutModalProps,
  TextWithValidation,
  Toggle,
} from "../../../../components";
import { Button, Form } from "react-bootstrap";
import { useEffect, useState } from "react";
import ProcessService from "../../../../services/ProcessService";
import { getUuid, propsFilter, showErrorMessage, showSuccessMessage } from "../../../../app/tools";
import { AdjustModel } from "../../../../data/entities";

export interface AdjustModalProps extends SlideOutModalProps {
  message?: string;
  confirmText?: string;
  range: { startPosition: number; endPosition: number };
  inspectionId: number;
  onAdjustmentComplete: () => void;
}

export function AdjustModal(props: AdjustModalProps) {
  const emptyAdjustModel : AdjustModel = {
    inspection: props.inspection,
    processes: props.processes,
    startPosition: props.range.startPosition,
    endPosition: props.range.endPosition,
    adjustFlaw: false,
    adjustFlw: false,
    adjustWall1: false,
    adjustWall2: false,
    flwVariance: undefined,
    flawVariance: undefined,
    wall1Variance: undefined,
    wall2Variance: undefined,
    flwAdjustment: undefined,
    flawAdjustment: undefined,
    wall1Adjustment: undefined,
    wall2Adjustment: undefined,
  };

  const [formData, setFormData] = useState<AdjustModel>(emptyAdjustModel);

  useEffect(() => {
    setFormData((current) => {
      return {
        ...current,
        startPosition: props.range.startPosition,
        endPosition: props.range.endPosition,
      };
    });

    return () => {};
  }, [props.range]);

  const onCancel = () => {
    props.showState.setter(false);
  };

  const onConfirm = (e: any) => {
    if (
      !window.confirm(
        "Are you sure you want to execute this adjustment process?"
      )
    )
      return;

    props.showState.setter(false);
    const adjustedData = async () => {
      const result = await new ProcessService().postProcessAdjust(
        props.inspectionId,
        formData
      );
      if (!result) {
        showErrorMessage(`Error adjusting data.`);
        return;
      }

      showSuccessMessage(
        `Adjusted data between ${formData.startPosition} and ${formData.endPosition}`
      );
      setFormData(emptyAdjustModel);
      props.onAdjustmentComplete();
    };

    adjustedData();
  };

  const renderFooter = () => (
    <div className="container p-0">
      <div className="row g-0">
        <div className="col-6 pe-2">
          <Button onClick={onCancel} variant="cancel" className="w-100">
            Cancel
          </Button>
        </div>
        <div className="col-6 ps-2">
          <Button onClick={onConfirm} variant="primary" className="w-100">
            {props.confirmText ?? "Submit"}
          </Button>
        </div>
      </div>
    </div>
  );

  const renderToggle = (
    description: string,
    propertyName: keyof AdjustModel
  ) => (
    <div className="col d-flex align-items-center">
      <Toggle
        description={description}
        id={propertyName}
        className="mb-3"
        initialValue={formData[propertyName] as boolean}
        onChange={(value) => {
          setFormData((current) => {
            return {
              ...current,
              [propertyName]: value,
            };
          });
        }}
      />
    </div>
  );

  const renderTextWithValidation = (
    propertyName: string,
    label: string,
    initialValue: number | undefined,
    targetName: keyof AdjustModel
  ) => (
    <div className={`col ${(formData[targetName] as boolean) ? "" : "invisible"}`}>
      <TextWithValidation
        id={getUuid()}
        label={label}
        type="number"
        initialValue={initialValue}
        onChangeText={(value) => {
          setFormData((current) => {
            return {
              ...current,
              [propertyName]: Number(value),
            };
          });
        }}
      />
    </div>
  );

  const filteredProps = propsFilter(props, [
    "setProcesses",
    "confirmText",
    "inspectionId",
    "onAdjustmentComplete",
  ]) as SlideOutModalProps;

  return (
    <SlideOutModal {...filteredProps} footer={renderFooter()}>
      <p>
        The next step is to pick a variance range to fluctuate the data
        randomly. This data processor will use the baseline settings for the
        inspection to normalize the data between points{" "}
        {props.range.startPosition} and {props.range.endPosition}, varying
        randomly by whatever settings you choose below.
      </p>
      <table className="table">
        <tbody>
          <tr>
            <td>Range: </td>
            <td>
              {props.range.startPosition} to {props.range.endPosition}
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td>Length: </td>
            <td>{props.range.endPosition - props.range.startPosition}</td>
          </tr>
        </tfoot>
      </table>
      <Form>
        {/* FLW Adjustment */}
        <div className="row pt-3 bg-light">
          {renderToggle("FLW Adjustment", "adjustFlw")}
          {renderTextWithValidation("flwVariance", "FLW Variance", formData.flwAdjustment, "adjustFlw")}
          {renderTextWithValidation("flwAdjustment", "FLW Adjustment", formData.flwAdjustment, "adjustFlw")}
        </div>
        {/* Flaw Adjustment */}
        <div className="row pt-3">
          {renderToggle("Flaw Adjustment", "adjustFlaw")}
          {renderTextWithValidation("flawVariance", "Flaw Variance", formData.flawAdjustment, "adjustFlaw")}
          {renderTextWithValidation("flawAdjustment", "Flaw Adjustment", formData.flawAdjustment, "adjustFlaw")}
        </div>
        {/* Wall1 Adjustment */}
        <div className="row pt-3 bg-light">
          {renderToggle("Wall1 Adjustment", "adjustWall1")}
          {renderTextWithValidation("wall1Variance", "Wall1 Variance", formData.wall1Adjustment, "adjustWall1")}
          {renderTextWithValidation("wall1Adjustment", "Wall1 Adjustment", formData.wall1Adjustment, "adjustWall1")}
        </div>
        {/* Wall2 Adjustment */}
        <div className="row pt-3">
          {renderToggle("Wall2 Adjustment", "adjustWall2")}
          {renderTextWithValidation("wall2Variance", "Wall2 Variance", formData.wall2Variance, "adjustWall2")}
          {renderTextWithValidation("wall2Adjustment", "Wall2 Adjustment", formData.wall2Adjustment, "adjustWall2")}
        </div>
      </Form>
    </SlideOutModal>
  );
}

export default AdjustModal;

